<template>
    <!-- The default <HeadMeta> in InitWrapper is hydrated lazily, so without LazyHydration here, the default will mount later and override meta -->
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMetaGame
            v-if="game && !game.gameUrlRedirectTarget"
            :breadcrumbs="breadcrumbs"
            :game="game"
            :gameCategoriesInfo="gameCategoriesInfo"
            :prefetchGameURL="buildGameIframeUrl(game)?.href"
        />
    </LazyHydration>
    <Layout :additionalCategories="linkedCategories">
        <div :class="$styleCrit.two_columns_wrapper">
            <main :class="$styleCrit.main">
                <div :class="$styleCrit.game_view">
                    <div :class="$styleCrit.game_screen">
                        <UnavailableGameContainer
                            v-if="game?.gameUrlRedirectTarget"
                            :game="game"
                            :categories="gameCategoriesInfo"
                            :unavailableGameReplace="game.gameUrlRedirectTarget && relatedGames?.[0]"
                        />

                        <GameContainer
                            v-else
                            :game="game"
                            :categories="gameCategoriesInfo"
                            @similarGamesPressed="scrollSimilarGames"
                        />
                    </div>
                    <RightSideCards
                        :games="recommendedGames || []"
                        :ctrSlateId="recommendationSlateId"
                        interfaceTarget="recommended"
                        disableMicrodata
                        :class="$styleCrit.desktop_override"
                    />
                </div>
                <div :class="$styleCrit.horizontal_ad">
                    <AdContainer
                        placement="leaderboard"
                        :class="$styleCrit.horizontal_ad_1"
                        :backgroundClass="$styleCrit.ad"
                        :emptyClass="$styleCrit.emptyAd"
                        respectHeight
                    />
                    <AdContainer
                        placement="leaderboard_rest"
                        :class="$styleCrit.horizontal_ad_2"
                        :backgroundClass="$styleCrit.ad"
                        :emptyClass="$styleCrit.emptyAd"
                        respectHeight
                    />
                </div>
                <LazyHydration
                    v-if="showRelatedGames"
                    :hydrate="hydrateOnIdle()"
                >
                    <div ref="related-games-shelf">
                        <GamePageRecommendedShelf
                            type="related"
                            data-interface-section="related"
                            :title="$tr('game.related')"
                            :games="relatedGames || placeholder"
                            disableMicrodata
                        />
                    </div>
                </LazyHydration>
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <GameDetails
                        v-if="game && gameCategoriesInfo && !game.gameUrlRedirectTarget"
                        :game="game"
                        :categoriesInfo="gameCategoriesInfo"
                        :breadcrumbs="breadcrumbs"
                        :leaderboard="leaderboard"
                    />
                    <PartnerBlock
                        v-if="game && showPartnerBlock"
                        :gameIcon="game.icon"
                        :hru="game.hru"
                    />
                </LazyHydration>
                <LazyHydration
                    v-if="seriesGameBlock"
                    :hydrate="hydrateOnIdle()"
                >
                    <GamePageRecommendedShelf
                        type="default"
                        :data-interface-section="seriesGameBlock.groupType"
                        :title="seriesGameBlock.title"
                        :games="seriesGameBlock.games || placeholder"
                        disableMicrodata
                    />
                </LazyHydration>
                <LazyHydration
                    v-if="developerGameBlock"
                    :hydrate="hydrateOnIdle()"
                >
                    <GamePageRecommendedShelf
                        type="default"
                        data-interface-section="developer"
                        :title="developerGameBlock.title"
                        :games="developerGameBlock.games"
                        disableMicrodata
                    />
                </LazyHydration>
                <LazyHydration
                    v-if="showRandomGames"
                    :hydrate="hydrateOnIdle()"
                >
                    <GamePageRecommendedShelf
                        type="random"
                        data-interface-section="random"
                        :title="$tr('game.random')"
                        :games="randomGames || placeholder"
                        disableMicrodata
                    />
                </LazyHydration>
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <PromoCategories />
                    <TheEnd />
                </LazyHydration>
                <ModalReportBug
                    :isOpen="isReportBugModalOpen"
                    :page="route.fullPath"
                    @closeReportModal="layoutStore.setIsReportBugModalOpen(false)"
                />
            </main>
            <RightColumn :class="$styleCrit.right_override" />
        </div>
    </Layout>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, hydrateOnIdle, onMounted, onServerPrefetch, useTemplateRef, toRef } from 'vue'
import { useRoute } from 'vue-router'

import AdContainer from '@/components/AdContainer/AdContainer.vue'
import LazyHydration from '@/components/LazyHydration.vue'
import { ModalReportBug } from '@/components/Modals/async/ModalReportBug'
import PromoCategories from '@/components/PromoCategories/PromoCategories.vue'
import RightColumn from '@/components/RightColumn.vue'
import TheEnd from '@/components/TheEnd/TheEnd.vue'
import { useTr } from '@/composables/use-tr'
import { DEFAULT_RECOMMENDATION_SIZE } from '@/constants/general'
import Layout from '@/layouts/Layout.vue'
import UnavailableGameContainer from '@/pages/Games/components/GameContainer/UnavailableGame/UnavailableGameContainer.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useSetAdTargetingOnMount } from '@/utils/vue-hooks/use-set-ad-targeting-on-mount'

import GameContainer from './components/GameContainer/GameContainer.vue'
import GameDetails from './components/GameDetails/GameDetails.vue'
import GamePageRecommendedShelf from './components/GamePageShelf/GamePageShelf.vue'
import HeadMetaGame from './components/HeadMetaGame.vue'
import PartnerBlock from './components/PartnerBlock/PartnerBlock.vue'
import RightSideCards from './components/RightSideCards.vue'
import { useGameIframeUrl } from './composables/use-game-iframe-url'
import { useGamePageData, useGameRecomendations } from './use-game-page-data'

const loggerStore = useLoggerStore()
const layoutStore = useLayoutStore()
const appStore = useAppStore()
const { buildGameIframeUrl } = useGameIframeUrl()

const route = useRoute()
const tr = useTr()

const placeholder: undefined[] = Array(DEFAULT_RECOMMENDATION_SIZE).fill(undefined)

const { isReportBugModalOpen } = storeToRefs(layoutStore)

const hru = toRef(() => route.params.game as string)

const { game, gameCategoriesInfo, leaderboard, preloadGameData, processGameResponse } = useGamePageData({
    hru,
    isExport: false,
})

const { recommendations, recommendationSlateId, linkedCategories, preloadRecommendations } = useGameRecomendations({
    hru,
})

const relatedShelfRef = useTemplateRef('related-games-shelf')

const seriesGameBlock = computed(() =>
    game.value?.series?.length && recommendations.value?.series?.length
        ? {
              title:
                  gameCategoriesInfo.value?.categoriesInfo?.find((cat) => cat.categoryType === 'series')?.title || '',
              games: recommendations.value.series,
              groupType: 'series',
              rows: 1,
          }
        : null,
)

const developerGameBlock = computed(() => {
    const developer = game.value?.developer?.trim()
    const games = recommendations.value?.developer
    return developer && games?.length
        ? {
              title: tr('game.games_by_developer', { developer }),
              games,
          }
        : null
})

const breadcrumbs = computed(() =>
    game?.value
        ? [
              {
                  title: tr('game.main_title'),
                  href: { name: ROUTE_NAMES.MAIN },
              },
              ...(gameCategoriesInfo.value?.pathCategories.map((cat) => ({ title: cat.title, href: cat.href })) || []),
              {
                  title: game.value?.title,
                  href: { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value?.hru } },
                  disabled: true,
              },
          ]
        : [],
)

const relatedGames = computed(() => recommendations.value?.related)
const showRelatedGames = computed(() => (relatedGames.value ? relatedGames.value.length > 0 : true))
const recommendedGames = computed(() => recommendations.value?.recommended)
const randomGames = computed(() => recommendations.value?.random)
const showRandomGames = computed(() => !appStore.isAppEnv && (randomGames.value ? randomGames.value.length > 0 : true))
const showPartnerBlock = computed(
    () => game.value?.is_in_export_catalog && game.value.source === 'playgama' && !appStore.isAppEnv,
)

function scrollSimilarGames() {
    relatedShelfRef.value?.scrollIntoView({ behavior: 'smooth' })
}

onServerPrefetch(async () => {
    await Promise.all([preloadGameData(), preloadRecommendations()])
    processGameResponse()
})

useSetAdTargetingOnMount(
    game.value && {
        content_category: game.value.main_category,
        provider: game.value.source || '',
        game_id: game.value.id,
    },
)

onMounted(async () => {
    await Promise.all([preloadGameData(), preloadRecommendations()])
    if (processGameResponse()) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'game_preview',
        })
    }
})
</script>
<style module="$styleCrit" critical>
/*
    Style for empty Ad block.
    Cannot not be just "display: none" because Ad container needs to be visible to react on size change.
*/
.emptyAd {
    margin-top: -12px;
}

.two_columns_wrapper {
    display: block;
}

.main {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

.right_override {
    display: none;
}

.horizontal_ad,
.horizontal_ad_1,
.horizontal_ad_2 {
    display: none;
}

.ad {
    background-color: var(--experimental-purple-graphit);
    border-radius: var(--radius-12);
}

.desktop_override {
    display: none;
}

@media (--desktop-small-plus) {
    .desktop_override {
        display: block;
    }
    .game_view {
        height: calc(100vw * 9 / 16);
        max-height: 461px;
        display: grid;
        grid-template-columns: 10fr 1fr;
        gap: 12px;
    }
    .game_screen {
        display: flex;
        min-width: 0;
        min-height: 0;
    }
    .horizontal_ad {
        display: flex;
        gap: 8px;
    }
    .horizontal_ad_1 {
        display: block;
        width: 100%;
    }
}

@media (--right-column-fits) {
    .game_view {
        height: calc(100vw * (9 / 16) - var(--global-right-pannel-width));
        min-height: 400px;
        max-height: none;
        grid-template-columns: 8fr 1fr;
    }

    .two_columns_wrapper {
        display: grid;
        grid-template-columns: 1fr var(--global-right-pannel-width);
        gap: 12px;
    }

    .right_override {
        display: block;
        flex-shrink: 0;
    }
}

@media (--desktop-medium-plus) {
    .game_view {
        grid-template-columns: 7fr 1fr;
    }
}

@media (--desktop-xlarge-plus) {
    .game_view {
        grid-template-columns: 4fr 1fr;
    }
    .game_view {
        max-height: 621px;
    }

    .horizontal_ad_1,
    .horizontal_ad_2 {
        display: block;
        flex-basis: 50%;
        flex-shrink: 0;
        width: 50%;
    }
}
</style>
